<template>
  <div class="video-navigation-container" :style="style">
    <transition name="expend">
      <div v-show="!isExpend && isMobile" class="expend-icon el-icon-d-arrow-right" @click="onExpend"></div>
    </transition>
    <div v-if="isExpend" class="video-desc">
      <van-swipe v-if="isMobile" class="desc-list" vertical :autoplay="4000" :height="100" :show-indicators="false" :touchable="false">
        <van-swipe-item v-for="(video, index) in videoList" class="desc-item" :key="index">
          <img class="avatar" :src="video.avatar" @click="openVideoSwiper(index)">
          <div class="desc" @click="openVideoSwiper(index)">
            <i class="el-icon-circle-close collapse-icon" @click.stop="onCollapse"></i>
            <i class="el-icon-caret-left left-arrow"></i>
            <span class="text">{{ video.description }}</span>
          </div>
        </van-swipe-item>
      </van-swipe>
      <el-carousel v-else class="desc-list pc" direction='vertical' indicator-position="none" :interval="4000" height="100">
        <el-carousel-item v-for="(video, index) in videoList" class="desc-item" :key="index">
          <div v-if="pcConfig.showText" class="desc" @click="openVideoSwiper(index)">
            <i class="el-icon-circle-close collapse-icon right-close-icon" @click.stop="onCollapse"></i>
            <i class="el-icon-caret-right right-arrow"></i>
            <span class="text">{{ video.description }}</span>
          </div>
          <img class="avatar" :src="video.avatar" @click="openVideoSwiper(index)">
        </el-carousel-item>
      </el-carousel>
    </div>
    <transition name="fade">
      <div v-show="showVideoSwiper" class="video-swiper">
        <van-swipe
          vertical
          class="video-list"
          height="100%"
          ref="swiper"
          :initial-swipe="initialSwipeIndex"
          :show-indicators="false"
          @change="handleVideoSwipeChange"
        >
          <van-swipe-item
            v-for="(video, index) in videoList"
            class="video-item"
            :key="index"
            @mousedown.native.stop="onMouseDown($event, index, video)"
          >
            <video
              playsinline
              webkit-playsinline
              x-webkit-airplay
              class="player"
              preload="metadata"
              :loop="true"
              :src="video.url"
              :ref="`video_player_${index}`"
              @timeupdate="timeupdate"
              @canplay="getTotal(index)"
            ></video>
            <div class="clickable-mask" @touchstart="togglePlay(index, video)">
              <div class="desc-container">
                <img class="avatar" :src="video.avatar">
                <div class="desc">
                  <span class="text">{{ video.description }}</span>
                </div>
              </div>
              <div v-if="videoList.length > 1" v-show="showSlideTip" class="slide-tip">
                <i class="el-icon-d-arrow-left slide-icon"></i>
                <span>{{ $t('admin.job.editor.message.videoEndedTip') }}</span>
              </div>
              <img v-show="video.paused" class="play-btn" src="@/assets/image/play-video-icon.png"/>
              <div class="video-progress" @touchstart.stop="onDragging" @touchend="onDragged">
                <!-- durationProgress / 100 为了让进度条运动的时候更加丝滑 -->
                <el-slider
                  v-model="currentTimeValMap[index]"
                  class="video-progress-bar"
                  :step="durationProgressMap[index] / 100"
                  :max="durationProgressMap[index]"
                  :show-tooltip="false"
                  @change="getNewTime"
                >
                </el-slider>
              </div>
            </div>
          </van-swipe-item>
        </van-swipe>
        <div class="close-btn el-icon-circle-close" @click="closeVideoSwiper"></div>
      </div>
    </transition>
  </div>
</template>

<script>
import _ from 'lodash'
import { SwipeAdapter } from '@/plugins/vant'
import { mapActions, mapGetters } from 'vuex'
import { deviceType } from '@/utils/common'

export default {
  props: {
    data: {
      type: Array
    },
    isMobile: {
      type: Boolean,
      default: true
    },
    hiddenVideoInPC: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      showVideoSwiper: false,
      showSlideTip: false,
      videoList: null,
      activeIndex: 0,
      initialSwipeIndex: 0,
      dragging: false,
      currentTime: 0, // 当前播放时间
      currentTimeValMap: {}, // 进度条当前播放时间，必须是number类型
      durationProgressMap: {}, // 进度条的最大值，必须是number类型
      swipeAdapter: null,
      pcConfig: {
        showText: true
      }
    }
  },
  computed: {
    ...mapGetters('personal', ['isExpend']),
    style () {
      if (this.isExpend) {
        return {
          left: 0,
          right: 0
        }
      }
      return {
        left: '-100%',
        right: '100%'
      }
    },
    swiper () {
      return this.$refs.swiper
    }
  },
  created () {

  },
  mounted () {
    if (!this.isMobile && !this.isExpend) {
      this.setVideoNavigationIsExpend(true)
    }
  },
  methods: {
    ...mapActions('personal', ['setVideoNavigationIsExpend']),
    formatVideoList () {
      this.videoList = _.map(this.data, item => {
        return {
          ...item,
          paused: false
        }
      })
    },
    closeVideoSwiper () {
      this.showVideoSwiper = false
      this.$emit('videoNavigationShowChange', false)

      this.resetVideoStatus()
    },
    /**
     * 重置各视频的状态：将播放进度设置为0，并且暂停播放
     */
    resetVideoStatus () {
      _.forEach(this.videoList, (video, index) => {
        const player = this.getVideoPlayer(index)
        player.currentTime = 0
        this.currentTimeValMap[index] = 0
        player.pause()
      })
    },
    /**
     * 点击头像或者描述打开视频导览
     * @param {number} index
     */
    openVideoSwiper (index) {
      if (this.hiddenVideoInPC && deviceType.isPc) {
        return
      }
      this.activeIndex = index
      this.initialSwipeIndex = index
      this.showVideoSwiper = true
      this.$emit('videoNavigationShowChange', true)
      this.showSlideTip = false
      this.$nextTick(() => {
        this.swiper.resize() // swipe组件从隐藏切换显示，一开始获取到的宽度为0，所以需要重绘正确计算位置
        this.togglePlay(index)
        _.forEach(this.data, (dataItem, dataIndex) => {
          if (dataIndex === index) {
            return
          }
          const player = this.getVideoPlayer(dataIndex)
          player.muted = true
          player.play()
          setTimeout(() => {
            player.pause()
            player.currentTime = 0
            player.muted = false
          }, 10)
        })
      })
    },
    /**
     * 控制视频的播放，暂停
     * @param {number} index 轮播的页index值，用来确定正在使用的是哪个video标签
     * @param {object} video 视频信息
     */
    async togglePlay (index, video) {
      const player = this.getVideoPlayer(index)
      if (!video) {
        video = this.videoList[index]
      }

      if (!player || !video) {
        return
      }

      if (player.paused) {
        try {
          player.muted = false
          await player.play()
          video.paused = false
        } catch (err) {
          player.load()
          video.paused = true
        }
      } else {
        player.pause()
        video.paused = true
      }
    },
    /**
     *  获取视频播放器
     * @param {number} index
     */
    getVideoPlayer (index) {
      return this.$refs[`video_player_${index}`][0]
    },
    onMouseDown (e, index, video) {
      if (this.hiddenVideoInPC && deviceType.isMobile) {
        return
      }
      if (!this.swipeAdapter) {
        this.swipeAdapter = new SwipeAdapter(this.swiper, this.onMouseClick)
      }
      this.swipeAdapter.onMouseDown(e, index, video)
    },
    onMouseClick (e, index, video) {
      this.togglePlay(index, video)
    },
    /**
     * 处理视频轮播页滑动后，暂停上一个视频，播放当前轮播页的视频
     * @param {number} index 滑动后，轮播页的Index值，可以用来确定当前的video播放器
     */
    handleVideoSwipeChange (index) {
      const prePlayer = this.getVideoPlayer(this.activeIndex)
      prePlayer.currentTime = 0
      prePlayer.pause()

      // 更新activeIndex为当前轮播页
      this.activeIndex = index
      this.togglePlay(index)

      // 隐藏滑动提示词
      this.showSlideTip = false
    },
    onVideoEnded () {
      this.showSlideTip = true
    },
    onCollapse () {
      if (this.hiddenVideoInPC && deviceType.isPc) {
        return
      }
      if (!this.isMobile) {
        this.pcConfig.showText = false
        return
      }
      this.setVideoNavigationIsExpend(false)
    },
    onExpend () {
      this.setVideoNavigationIsExpend(true)
    },
    // 自定义进度条------------------------------------------------
    timeupdate () {
      const player = this.getVideoPlayer(this.activeIndex)
      if (!this.dragging) {
        this.currentTimeValMap[this.activeIndex] = player.currentTime
      }

      // 是否播完
      const isEnded = player.currentTime >= player.duration - 1
      if (isEnded) {
        this.onVideoEnded()
      }
    },
    // 进度条拖动时间
    getNewTime (val) {
      const player = this.getVideoPlayer(this.activeIndex)
      player.currentTime = val
    },
    // 获取总时长
    getTotal (index) {
      const player = this.getVideoPlayer(index)
      this.durationProgressMap[index] = player.duration
    },
    onDragging () {
      this.dragging = true
    },
    onDragged () {
      this.dragging = false
    }
  },
  watch: {
    data: {
      handler () {
        this.formatVideoList()
      },
      immediate: true
    }
  }
}
</script>
<style lang="scss" scoped>
.video-navigation-container {
  position: fixed;
  margin: 15px 30px 15px 25px;
  left: 0;
  bottom: 65px;
  transition: all .5s;

  .expend-icon {
    position: fixed;
    left: 0;
    bottom: 120px;
    height: 50px;
    line-height: 50px;
    border-radius: 0 15px 15px 0;
    color: #fff;
    background-color: var(--custom-color, #fc8434);
    transition: .2s;
    font-size: 14px;
    cursor: pointer;
  }

  .video-desc {
    .desc-list {
      height: 100px;

      ::v-deep {
        .el-carousel__container {
          height: 100px;
        }
      }

      .desc-item {
        display: flex;
        align-items: center;

        .avatar {
          margin-left: 2px;
          flex-shrink: 0;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: cover;
        }

        .desc {
          position: relative;
          margin: 8px;
          margin-left: 18px;
          padding: 15px 10px;
          color: #fff;
          background-color: var(--custom-color, #fc8434);
          border-radius: 8px;

          .text {
            word-break: break-word;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 18px;
            font-size: 14px;
          }

          .left-arrow {
            position: absolute;
            font-size: 24px;
            left: -15px;
            top: 50%;
            transform: translateY(-50%);
            color: var(--custom-color, #fc8434);
          }

          .right-arrow {
            position: absolute;
            font-size: 24px;
            right: -15px;
            top: 50%;
            transform: translateY(-50%);
            color: var(--custom-color, #fc8434);
          }

          .collapse-icon {
            position: absolute;
            font-size: 22px;
            right: -6px;
            top: -8px;
            text-shadow: 0 0 4px rgba(0, 0, 0, .8);
          }

          .right-close-icon {
            left: -6px;
          }
        }
      }
    }
    .pc {
      .desc-item {
        justify-content: flex-end;
        .avatar {
          margin-left: 10px;
        }
      }
    }
  }

  .video-swiper {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;

    .video-list {
      height: 100%;

      .video-item {
        position: relative;
        display: flex;
        align-items: center;
        background-color: #000;

        .player {
          width: 100%;
          height: 100%;
        }

        .clickable-mask {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: calc(100% - 30px);
          z-index: 1;

          .desc-container {
            display: flex;
            align-items: center;
            position: absolute;
            left: 10px;
            bottom: 22px;

            .avatar {
              width: 60px;
              height: 60px;
              border-radius: 50%;
              flex-shrink: 0;
              object-fit: cover;
            }

            .desc {
              display: flex;
              align-items: center;
              margin-left: 10px;
              padding: 5px;
              height: 48px;
              font-size: 16px;
              line-height: 20px;
            }

            .text {
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 3;
              overflow: hidden;
              text-overflow: ellipsis;
              color: #fff;
            }
          }

          .slide-tip {
            color: #fff;
            position: absolute;
            left: 0;
            bottom: 8px;
            text-align: center;
            font-size: 12px;
            width: 100%;
            line-height: 1;
            opacity: .5;

            .slide-icon {
              transform: rotate(90deg);
              margin-right: 5px;
            }
          }

          .play-btn {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: #fff;
            width: 70px;
            height: 70px;
          }

          .video-progress {
            position: absolute;
            left: 10px;
            right: 10px;
            bottom: 7px;
            height: 10px;

            .video-progress-bar {
              ::v-deep {
                .el-slider__runway {
                  background-color: rgba(255, 255, 255, .5);

                  .el-slider__bar {
                    background-color: rgba(255, 255, 255, .65);
                  }

                  .el-slider__button-wrapper {
                    top: -13px;
                    width: 32px;
                    height: 32px;

                    .el-slider__button {
                      border: none;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    .close-btn {
      position: fixed;
      top: 10px;
      right: 10px;
      color: #fff;
      font-size: 40px;
      z-index: 1;
      padding: 10px;
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .6s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.expend-enter-active, .expend-leave-active {
  transform: translateX(0);
}
.expend-enter, .expend-leave-to {
  transform: translateX(-16px);
}
</style>
